import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import type { ErrorStateDetails } from '#src/app/shared/utils/async';
import { isError, isLoaded } from '#src/app/shared/utils/async';

import type {
	HomepagePageCombinedState,
	HomepagePagePrefetchedContext,
	HomepagePagePrefetchedUserContext,
} from './hook';
import { usePageContext } from './hook';
import { HomepagePageDataContextProvider } from './pageData';
import { HomepageTrendingSectionDataContextProvider } from './pageTrendingSectionData';
import { HomepagePageUserDataContextProvider } from './pageUserData';

export type ErrorHandlerProps = { context: ErrorStateDetails<HomepagePageCombinedState> };

type Props = {
	prefetchedData: HomepagePagePrefetchedContext | undefined;
	prefetchedUserData: HomepagePagePrefetchedUserContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const HomepagePageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	prefetchedData,
	prefetchedUserData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(prefetchedData, prefetchedUserData);
	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageUserData, pageData, trendingSectionData } = pageContextState;

	return (
		<HomepagePageDataContextProvider pageData={pageData}>
			<HomepagePageUserDataContextProvider pageUserData={pageUserData}>
				<HomepageTrendingSectionDataContextProvider trendingSectionData={trendingSectionData}>
					<TrackingContext properties={{ version: 'v3' }}>{children}</TrackingContext>
				</HomepageTrendingSectionDataContextProvider>
			</HomepagePageUserDataContextProvider>
		</HomepagePageDataContextProvider>
	);
};
